import {
  ListboxInput,
  ListboxPopover,
  ListboxList,
  ListboxButton,
  useListboxContext,
} from '@reach/listbox';
import { useCallback, useEffect } from 'react';
import classNames from 'classnames';

import SvgIcon from '../../SvgIcon';

import IDropdown from './types';
import useStyles from './styles';

import '@reach/listbox/styles.css';

function Dropdown({
  className,
  children,
  mRef,
  value,
  label,
  name,
  size = 'small',
  disabled = false,
  hasBorder = true,
  svgIconName = 'arrow-down-gen',
  buttonClassName,
  popoverStyle,
  popoverClassName,
  onChange,
  onToggle,
  listboxButton,
}: IDropdown) {
  const classes = useStyles({ size, hasBorder });

  const handleOnChange = useCallback(
    (optionValue: string) => {
      if (!disabled) {
        onChange(optionValue);
      }
    },
    [disabled, onChange],
  );

  return (
    <>
      {label && (
        <label className={classes.label} htmlFor={name}>
          {label}
        </label>
      )}
      <ListboxInput
        className={className}
        ref={mRef}
        onChange={handleOnChange}
        defaultValue={value}
        value={value}
        disabled={disabled}
      >
        <ListBoxContainer
          popoverClassName={popoverClassName}
          popoverStyle={popoverStyle}
          buttonClassName={buttonClassName}
          disabled={disabled}
          size={size}
          onToggle={onToggle}
          hasBorder={hasBorder}
          svgIconName={svgIconName}
          listboxButton={listboxButton}
        >
          {children}
        </ListBoxContainer>
      </ListboxInput>
    </>
  );
}

function ListBoxContainer({
  disabled,
  hasBorder,
  children,
  size,
  svgIconName,
  buttonClassName,
  popoverStyle,
  popoverClassName,
  onToggle,
  listboxButton,
}: Pick<
  IDropdown,
  | 'disabled'
  | 'children'
  | 'size'
  | 'onToggle'
  | 'hasBorder'
  | 'svgIconName'
  | 'buttonClassName'
  | 'popoverStyle'
  | 'popoverClassName'
  | 'listboxButton'
>) {
  const classes = useStyles({ size, hasBorder });
  const { isExpanded } = useListboxContext();

  useEffect(() => {
    onToggle?.(isExpanded);
  }, [isExpanded, onToggle]);

  return (
    <>
      <ListboxButton
        className={classNames(classes.button, disabled ? 'disabled' : '', buttonClassName)}
        arrow={listboxButton ?? <SvgIcon name={svgIconName} />}
      />
      <ListboxPopover
        style={popoverStyle}
        className={classNames(classes.popover, popoverClassName)}
      >
        <ListboxList>{children}</ListboxList>
      </ListboxPopover>
    </>
  );
}

export default Dropdown;
